import { useEffect } from 'react';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getNotificationCenter,
  markNotificationsSeen,
  NotificationCenter,
} from '../../../api/notificationCenter';

export const NotificationCenterQueryKey = 'NOTIFICATION_CENTER';

/** The bell's own «seen», which changes nothing in the modules and so does not ask again */
export const MarkSeenMutationKey = [NotificationCenterQueryKey, 'seen'];

/** Often enough to feel live, without asking every module all the time */
export const NOTIFICATION_CENTER_INTERVAL = 30_000;

/** Changes that come together, like a save that makes several calls, are asked about once */
export const CHANGE_DELAY = 1_000;

const pending = new WeakMap<QueryClient, ReturnType<typeof setTimeout>>();

/** One question after a burst of changes, however many bells and pages listen */
const askAgainSoon = (queryClient: QueryClient) => {
  clearTimeout(pending.get(queryClient));
  pending.set(
    queryClient,
    setTimeout(() => {
      pending.delete(queryClient);
      void queryClient.invalidateQueries({ queryKey: [NotificationCenterQueryKey] });
    }, CHANGE_DELAY),
  );
};

/**
 * What waits for the user across the modules. Fetched every 30 seconds while the tab is visible, when the user comes
 * back to it, and shortly after a change in the app, as a change anywhere can finish a task.
 */
export const useNotificationCenterQuery = (
  language: string,
  enabled = true,
) => {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    return queryClient.getMutationCache().subscribe((event) => {
      if (
        event.type === 'updated' &&
        event.action.type === 'success' &&
        event.mutation.options.mutationKey?.join() !== MarkSeenMutationKey.join()
      )
        askAgainSoon(queryClient);
    });
  }, [enabled, queryClient]);
  return useQuery({
    queryKey: [NotificationCenterQueryKey, language],
    queryFn: () => getNotificationCenter(language),
    enabled,
    refetchInterval: NOTIFICATION_CENTER_INTERVAL,
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: true,
    staleTime: 10_000,
  });
};

/**
 * Opening the bell takes away its count at once, as in other apps, and the server remembers it for the user's other
 * devices. What comes after counts again. If the server does not take it, the count comes back as it was, and is
 * taken away the next time the bell is opened or closed: nothing is sent again by itself.
 */
export const useMarkNotificationsSeenMutation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: MarkSeenMutationKey,
    mutationFn: (keys: string[]) => markNotificationsSeen(keys),
    onMutate: async (keys) => {
      // A fetch on its way must not bring the old count back
      await queryClient.cancelQueries({ queryKey: [NotificationCenterQueryKey] });
      const previous = queryClient.getQueriesData<NotificationCenter>({ queryKey: [NotificationCenterQueryKey] });
      queryClient.setQueriesData<NotificationCenter>(
        { queryKey: [NotificationCenterQueryKey] },
        (center) => (center ? { ...center, seen: keys } : center),
      );
      return { previous };
    },
    onError: (_error, _keys, context) => {
      context?.previous.forEach(([queryKey, center]) => queryClient.setQueryData(queryKey, center));
    },
  });
};
